import type { ReactNode } from "react";
import clsx from "clsx";
import { getHeroImagePath } from "../utils/assets";
import { formatExactNumber } from "../utils/formatNumber";
import type { PlayerProfile } from "../types";
import { PlayerNameBadges } from "./PlayerNameBadges";
import { ModalBackdrop } from "./ModalBackdrop";

interface PlayerProfileModalProps {
  profile: PlayerProfile;
  onClose: () => void;
  footer?: ReactNode;
}

export function PlayerProfileModal({ profile, onClose, footer }: PlayerProfileModalProps) {
  const powerLabel = profile.hero_class === "MAGE" ? "Intellect" : "Power";
  const mainAtkLabel = profile.hero_class === "MAGE" ? "MATK" : "PATK";
  const mainAtkValue = profile.hero_class === "MAGE" ? profile.stats.matk : profile.stats.patk;

  return (
    <ModalBackdrop onClose={onClose} className="z-50 items-end bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-kingdom-purple border border-kingdom-light rounded-2xl p-4 space-y-4 shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src={getHeroImagePath(profile.hero_class, profile.gender)}
              alt={profile.display_name}
              className="w-14 h-14 rounded-xl border border-kingdom-light object-cover object-top shrink-0"
            />
            <div className="min-w-0">
              <PlayerNameBadges
                name={profile.display_name}
                guildName={profile.guild_name}
                vipSubscriptionActive={profile.vip_subscription_active}
                proActive={profile.pro_active}
                size="md"
                nameClassName="truncate block"
              />
              <div className="text-xs text-gray-400">
                {profile.hero_class} · Lv.{profile.level}
              </div>
              <div className="text-xs text-gray-500">Formation: {profile.formation}</div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white text-xl leading-none">
            ✕
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <BrStat label="Battle Rating" value={formatExactNumber(profile.battle_rating)} highlight />
          <BrStat label="Rank" value={profile.rank ? `#${profile.rank}` : "—"} />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <BrStat label={powerLabel} value={String(profile.stats.power)} />
          <BrStat label="Armor" value={String(profile.stats.armor)} />
          <BrStat label="Endurance" value={String(profile.stats.endurance)} />
          <BrStat label="CRIT" value={`${profile.stats.crit}%`} />
          <BrStat label="HP" value={formatExactNumber(profile.stats.hp)} />
          <BrStat label={mainAtkLabel} value={formatExactNumber(mainAtkValue)} />
          <BrStat label="PDEF" value={formatExactNumber(profile.stats.pdef)} />
          <BrStat label="MDEF" value={formatExactNumber(profile.stats.mdef)} />
        </div>

        {footer}
      </div>
    </ModalBackdrop>
  );
}

function BrStat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={clsx(
        "rounded-lg px-3 py-2",
        highlight ? "bg-gold-500/10 border border-gold-500/30" : "bg-kingdom-dark",
      )}
    >
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={clsx("font-bold tabular-nums", highlight ? "text-gold-300" : "text-white")}>{value}</div>
    </div>
  );
}
